import React from "react";
import { useQuery } from "@tanstack/react-query";
import CustomModal from "../../common/CustomModal";
import StatusChip from "../../common/StatusChip";
import ButtonLoader from "../../Icons/ButtonLoader";
import AdsAPIs from "../../../apiRoutes/Ads";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { GET_AD_REQUEST_QUERY_KEY } from "../../../utils/constants/queryKeys";
import { formatDisplayDateTime } from "../../../utils/helperFunctions";
import { MODERATION_CHIP } from "../ModerationHookDots";
import { IssueChips } from "../../AdModeration/ModerationIssues";

const HookRow = ({ hook }) => {
  const m = hook.moderation || {};
  return (
    <div className="grid grid-cols-[64px_minmax(0,1fr)_auto] items-start gap-3 border-b border-gray-100 py-3 last:border-b-0">
      <span className="pt-0.5 text-sm font-semibold text-gray-800">Hook {hook.hook_number}</span>
      <div className="min-w-0 space-y-1.5">
        <p className="truncate font-mono text-xs text-gray-500" title={hook.ad_name}>
          {hook.ad_name || "Not delivered yet"}
        </p>
        {(m.moderated_by || m.finalized_at) && (
          <p className="text-xs text-gray-400">
            {m.moderated_by && <>By {m.moderated_by}</>}
            {m.moderated_by && m.finalized_at && " · "}
            {m.finalized_at && <>finalized {formatDisplayDateTime(m.finalized_at)}</>}
          </p>
        )}
        <IssueChips codes={m.issues_highlighted} />
        {m.override && (
          <p className="text-xs text-emerald-700">
            Overridden by {m.override.by || "requester"}
            {m.override.at ? ` · ${formatDisplayDateTime(m.override.at)}` : ""}: {m.override.reason}
          </p>
        )}
      </div>
      <StatusChip subtle status={MODERATION_CHIP[m.state] ?? m.state} />
    </div>
  );
};

/** Per-hook moderation of one request: decision, who and when, and what was flagged. */
const ModerationStatusModal = ({ open, onClose, requestId, adsetName }) => {
  const axiosPrivate = useAxiosPrivate();
  const { data: detail, isLoading } = useQuery({
    queryKey: [...GET_AD_REQUEST_QUERY_KEY, requestId],
    queryFn: () => AdsAPIs.getAdRequest({ axiosPrivate, id: requestId }),
    enabled: open && !!requestId,
  });
  const hooks = detail?.hook_states ?? [];
  return (
    <CustomModal open={open} title="Moderation status" onClose={onClose} isButtonsRequired={false} className="lg:!w-[720px]">
      <div className="py-2">
        {adsetName && <p className="mb-3 break-all font-mono text-xs text-gray-500">{adsetName}</p>}
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-10">
            <ButtonLoader color="#7f7ad5" />
            <span className="text-sm text-gray-500">Loading hooks…</span>
          </div>
        ) : hooks.length ? (
          <div className="rounded-lg border border-gray-200 bg-white px-4">
            {hooks.map((h) => (
              <HookRow key={h.hook_number} hook={h} />
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">No hooks found for this request.</p>
        )}
      </div>
    </CustomModal>
  );
};

export default ModerationStatusModal;
